import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import EOIShare from './EOIShare';

// Two-part EOI. Part 1: who is booking (contact) and the booking names (up to 2,
// each with relation to the contact — e.g. flat in mother's name). Part 2: typology,
// floor, cheque (photo + details), source and the declaration. Prefilled from the CRM.
const RELATIONS = ['Self', 'Mother', 'Father', 'Spouse', 'Son', 'Daughter', 'Brother', 'Sister', 'Other'];
const S = {
  back: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 10px' },
  modal: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '640px', padding: '20px' },
  steps: { display: 'flex', gap: '6px', margin: '12px 0 4px' },
  stepOn: (on) => ({ flex: 1, height: '6px', borderRadius: '3px', background: on ? '#2563eb' : '#e5e7eb' }),
  h: { fontSize: '15px', fontWeight: 800, margin: '18px 0 8px', color: '#111827' },
  card: { border: '1px solid #e5e7eb', borderRadius: '12px', padding: '12px', marginBottom: '10px', background: '#f9fafb' },
  lbl: { display: 'block', fontSize: '13px', fontWeight: 700, color: '#374151', margin: '8px 0 4px' },
  in: { width: '100%', padding: '11px 12px', border: '1.5px solid #d1d5db', borderRadius: '10px', fontSize: '16px', boxSizing: 'border-box', background: '#fff' },
  two: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 10px' },
  chip: (on) => ({ padding: '11px 14px', borderRadius: '999px', border: `1.5px solid ${on ? '#2563eb' : '#d1d5db'}`, background: on ? '#eff6ff' : '#fff', color: on ? '#1d4ed8' : '#374151', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }),
  primary: { flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: '#2563eb', color: '#fff', fontSize: '17px', fontWeight: 800, cursor: 'pointer' },
  green: { flex: 1, padding: '14px', borderRadius: '12px', border: 'none', background: '#16a34a', color: '#fff', fontSize: '17px', fontWeight: 800, cursor: 'pointer' },
  ghost: { padding: '14px 18px', borderRadius: '12px', border: '1px solid #d1d5db', background: '#fff', fontSize: '15px', cursor: 'pointer' },
  err: { background: '#fef2f2', color: '#b91c1c', padding: '10px 12px', borderRadius: '10px', fontSize: '14px', marginTop: '10px' },
  link: { background: 'none', border: 'none', color: '#2563eb', fontSize: '14px', fontWeight: 600, cursor: 'pointer', padding: '4px 0' },
};
const EMPTY_APP = { name: '', relation: 'Self', mobile: '', email: '', dob: '', pan: '', passport: '', oci: '' };
const PAY = [['cheque', 'Cheque'], ['cheque_courier', 'Cheque by courier'], ['bank_transfer', 'Bank transfer']];

function shrink(file, maxSide = 1800) {
  return new Promise((resolve) => {
    if (!file || !/^image\/(jpeg|png|webp)$/i.test(file.type) || file.size < 600 * 1024) return resolve(file);
    const img = new Image(); const url = URL.createObjectURL(file);
    img.onload = () => {
      const k = Math.min(1, maxSide / Math.max(img.width, img.height));
      const c = document.createElement('canvas'); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => { URL.revokeObjectURL(url); resolve(b ? new File([b], 'cheque.jpg', { type: 'image/jpeg' }) : file); }, 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export const openEoiPrint = async (apiBase, id) => {
  const w = window.open('', '_blank');
  try {
    const r = await axios.get(`${apiBase}/eoi-form/${id}/print`, { responseType: 'text' });
    if (w) { w.document.open(); w.document.write(r.data); w.document.close(); }
  } catch (e) { if (w) w.close(); alert('Could not open the printable EOI.'); }
};

const EOIForm = ({ apiBase, clientId, eoiId, onClose }) => {
  const [step, setStep] = useState(1);
  const [opt, setOpt] = useState(null);
  const [f, setF] = useState(null);
  const [partners, setPartners] = useState([]);
  const [chequeFile, setChequeFile] = useState(null);
  const [chequePreview, setChequePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(null);
  const [sharing, setSharing] = useState(false);
  const camRef = useRef(null);

  useEffect(() => {
    axios.get(`${apiBase}/booking-flow/brokers`).then((r) => setPartners(r.data || [])).catch(() => {});
    if (eoiId) {
      Promise.all([axios.get(`${apiBase}/eoi-form/options`), axios.get(`${apiBase}/eoi-form/${eoiId}`)]).then(([o, e]) => {
        const x = e.data;
        setOpt(o.data);
        setF({
          client_id: x.client_id, contact_name: x.contact_name || '', contact_mobile: x.contact_mobile || '',
          applicants: (x.applicants && x.applicants.length ? x.applicants : [{ ...EMPTY_APP, name: x.applicant_name || '' }]).map((a) => ({ ...EMPTY_APP, ...a, dob: a.dob || '', pan: a.pan || '' })),
          address: x.applicant_address || '', city: x.city || '', pincode: x.pincode || '', country: x.country || 'India', residential_status: x.residential_status || 'Resident Indian',
          typology: x.typology || '', preferred_floor: x.preferred_floor || '', cheque_number: x.reference_number || '', cheque_bank: x.cheque_bank || '',
          cheque_branch: x.cheque_branch || '', amount: x.eoi_amount || '', cheque_date: x.cheque_date ? String(x.cheque_date).slice(0, 10) : '',
          payment_method: x.payment_method || 'cheque', utr_number: x.utr_number || '', transfer_date: x.transfer_date ? String(x.transfer_date).slice(0, 10) : '',
          source: x.source || 'direct', channel_partner_id: x.channel_partner_id || '', broker_name: '', broker_firm: '', broker_rera: '', broker_mobile: '',
          declaration_accepted: !!x.declaration_accepted, office_checks: x.office_checks || {}, notes: x.notes || '', eoi_number: x.eoi_number,
        });
      }).catch(() => setErr('Could not open this EOI.'));
    } else {
      axios.get(`${apiBase}/eoi-form/prefill/${clientId}`).then((r) => {
        const p = r.data;
        setOpt(p.options);
        setF({
          client_id: p.client_id, contact_name: p.contact_name || '', contact_mobile: p.contact_mobile || '',
          applicants: [{ ...EMPTY_APP, ...p.self }], address: p.address, city: p.city, pincode: '', country: p.country, residential_status: p.residential_status,
          typology: '', preferred_floor: '', cheque_number: '', cheque_bank: '', cheque_branch: '', amount: '', cheque_date: new Date().toISOString().slice(0, 10),
          payment_method: 'cheque', utr_number: '', transfer_date: '',
          source: p.source, channel_partner_id: p.channel_partner_id || '', broker_name: '', broker_firm: '', broker_rera: '', broker_mobile: '',
          declaration_accepted: false, office_checks: {}, notes: '', self: p.self,
        });
      }).catch(() => setErr('Could not load this client.'));
    }
  }, [apiBase, clientId, eoiId]);

  if (!f || !opt) {
    return <div style={S.back}><div style={S.modal}>{err ? <div style={S.err}>{err}</div> : 'Loading…'}<button style={{ ...S.ghost, marginTop: 12 }} onClick={() => onClose(false)}>Close</button></div></div>;
  }

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setApp = (i, k, v) => setF({ ...f, applicants: f.applicants.map((a, j) => (j === i ? { ...a, [k]: v } : a)) });
  const I = (label, value, onChange, props = {}) => (
    <div><label style={S.lbl}>{label}</label><input style={S.in} value={value ?? ''} onChange={onChange} {...props} /></div>
  );

  const next = () => {
    if (!f.contact_name.trim() || String(f.contact_mobile).replace(/\D/g, '').length < 8) { setErr('Enter the contact person\'s name and mobile (with country code if abroad).'); return; }
    if (!f.applicants[0]?.name?.trim()) { setErr('Enter at least one booking name.'); return; }
    setErr(''); setStep(2); document.querySelector('[data-eoi-top]')?.scrollIntoView();
  };

  const save = async () => {
    if (!f.declaration_accepted) { setErr('Tick the declaration to continue.'); return; }
    if (f.source === 'channel_partner' && !f.channel_partner_id && !f.broker_name.trim()) { setErr('Pick the channel partner, or enter their details.'); return; }
    setBusy(true); setErr('');
    const body = { ...f, applicants: f.applicants.filter((a) => a.name.trim()), channel_partner_id: f.channel_partner_id ? Number(f.channel_partner_id) : null };
    try {
      const r = eoiId ? await axios.put(`${apiBase}/eoi-form/${eoiId}`, body) : await axios.post(`${apiBase}/eoi-form`, body);
      const id = eoiId || r.data.id;
      if (chequeFile && f.client_id) {
        const fd = new FormData();
        fd.append('file', chequeFile); fd.append('doc_type', 'cheque');
        if (f.amount) fd.append('amount', f.amount);
        if (f.cheque_number) fd.append('cheque_number', f.cheque_number);
        if (f.cheque_bank) fd.append('bank_name', f.cheque_bank);
        if (f.cheque_date) fd.append('cheque_date', f.cheque_date);
        fd.append('notes', `EOI ${r.data.eoi_number || f.eoi_number || ''} — favouring ${opt.favouring}`);
        try {
          const up = await axios.post(`${apiBase}/client-docs/${f.client_id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (up.data.payment_id) await axios.put(`${apiBase}/eoi-form/${id}/payment`, { payment_id: up.data.payment_id });
        } catch (e) { setErr('EOI saved, but the cheque photo did not upload — add it from the client page.'); }
      }
      setSaved({ id, number: r.data.eoi_number || f.eoi_number });
    } catch (e) {
      setErr(e.response?.status === 403 ? 'You are not allowed to create EOIs.' : (e.response?.data?.error || 'Could not save. Check the internet and try again.'));
    } finally { setBusy(false); }
  };

  if (saved) {
    return (
      <div style={S.back}>
        <div style={{ ...S.modal, textAlign: 'center' }}>
          <div style={{ fontSize: '56px' }}>✅</div>
          <div style={{ fontSize: '22px', fontWeight: 800, color: '#16a34a' }}>EOI {saved.number} saved</div>
          <div style={{ color: '#4b5563', margin: '6px 0 16px' }}>{f.applicants.filter((a) => a.name).map((a) => a.name).join(' & ')} · {f.typology || 'typology not set'}</div>
          {err && <div style={S.err}>{err}</div>}
          {sharing && <EOIShare apiBase={apiBase} eoiId={saved.id} onClose={() => setSharing(false)} />}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button style={S.primary} onClick={() => openEoiPrint(apiBase, saved.id)}>🖨️ Print for signature</button>
            <button style={{ ...S.primary, background: '#25D366' }} onClick={() => setSharing(true)}>📤 Share</button>
            <button style={S.ghost} onClick={() => onClose(true)}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.back}>
      <div style={S.modal} data-eoi-top>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>📝 {eoiId ? `Edit EOI ${f.eoi_number || ''}` : 'New EOI'} · Kohinoor @ Patel Estate</h2>
          <button onClick={() => onClose(false)} style={{ border: 'none', background: 'none', fontSize: '28px', cursor: 'pointer', color: '#6b7280' }} aria-label="Close">×</button>
        </div>
        <div style={S.steps}><div style={S.stepOn(true)} /><div style={S.stepOn(step === 2)} /></div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>Step {step} of 2 · {step === 1 ? 'Who is booking' : 'Interest & payment'}</div>

        {step === 1 && (
          <>
            <div style={S.h}>Contact (the person booking)</div>
            <div style={S.two}>
              {I('Name *', f.contact_name, set('contact_name'))}
              {I('Mobile * (country code if abroad)', f.contact_mobile, set('contact_mobile'), { inputMode: 'tel', placeholder: '98200 00000 or +971 50 123 4567' })}
            </div>

            <div style={S.h}>Booking names (the flat will be in these names)</div>
            {f.applicants.map((a, i) => (
              <div key={i} style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <b>Applicant {i + 1}{i === 0 ? ' (primary)' : ' (joint)'}</b>
                  {i === 1 && <button style={{ ...S.link, color: '#dc2626' }} onClick={() => setF({ ...f, applicants: f.applicants.slice(0, 1) })}>Remove</button>}
                </div>
                <label style={S.lbl}>Relation to {f.contact_name || 'the contact'}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {RELATIONS.map((r) => (
                    <button key={r} style={{ ...S.chip(a.relation === r), padding: '7px 11px', fontSize: '14px' }}
                      onClick={() => {
                        if (r === 'Self' && f.self) setF({ ...f, applicants: f.applicants.map((x, j) => (j === i ? { ...EMPTY_APP, ...f.self } : x)) });
                        else setApp(i, 'relation', r);
                      }}>{r}</button>
                  ))}
                </div>
                <div style={S.two}>
                  {I('Full name (as on PAN) *', a.name, (e) => setApp(i, 'name', e.target.value))}
                  {I('Mobile', a.mobile, (e) => setApp(i, 'mobile', e.target.value), { inputMode: 'tel' })}
                  {I('Date of birth', a.dob, (e) => setApp(i, 'dob', e.target.value), { type: 'date' })}
                  {I('PAN', a.pan, (e) => setApp(i, 'pan', e.target.value.toUpperCase()), { placeholder: 'ABCDE1234F', style: { ...S.in, textTransform: 'uppercase' } })}
                </div>
                {I('Email', a.email, (e) => setApp(i, 'email', e.target.value), { type: 'email' })}
                {f.residential_status !== 'Resident Indian' && (
                  <div style={S.two}>
                    {I('Passport no.', a.passport, (e) => setApp(i, 'passport', e.target.value.toUpperCase()))}
                    {I('OCI / PIO card no.', a.oci, (e) => setApp(i, 'oci', e.target.value))}
                  </div>
                )}
              </div>
            ))}
            {f.applicants.length < 2 && (
              <button style={S.link} onClick={() => setF({ ...f, applicants: [...f.applicants, { ...EMPTY_APP, relation: 'Spouse' }] })}>+ Add a second (joint) applicant</button>
            )}

            <div style={S.h}>Address</div>
            {I('Address', f.address, set('address'))}
            <div style={S.two}>
              {I('City', f.city, set('city'))}
              {I('Pin', f.pincode, set('pincode'), { inputMode: 'numeric' })}
              {I('Country', f.country, set('country'))}
            </div>
            <label style={S.lbl}>Residential status</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {opt.residency.map((r) => <button key={r} style={S.chip(f.residential_status === r)} onClick={() => setF({ ...f, residential_status: r })}>{r}</button>)}
            </div>

            {err && <div style={S.err}>{err}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '18px' }}>
              <button style={S.ghost} onClick={() => onClose(false)}>Cancel</button>
              <button style={S.primary} onClick={next}>Next: interest & payment →</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={S.h}>Typology</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {opt.typologies.map((t) => <button key={t} style={S.chip(f.typology === t)} onClick={() => setF({ ...f, typology: t })}>{t}</button>)}
            </div>
            <div style={S.h}>Preferred floor</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {opt.floors.map((t) => <button key={t} style={S.chip(f.preferred_floor === t)} onClick={() => setF({ ...f, preferred_floor: t })}>{t}</button>)}
            </div>

            <div style={S.h}>Payment</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
              {PAY.map(([k, l]) => <button key={k} style={S.chip(f.payment_method === k)} onClick={() => setF({ ...f, payment_method: k })}>{l}</button>)}
            </div>
            {f.payment_method === 'bank_transfer' && (
              <div style={S.two}>
                {I('Amount (₹)', f.amount, set('amount'), { type: 'number', inputMode: 'numeric' })}
                {I('UTR / reference no.', f.utr_number, (e) => setF({ ...f, utr_number: e.target.value.toUpperCase() }))}
                {I('Transfer date', f.transfer_date, set('transfer_date'), { type: 'date' })}
              </div>
            )}
            {f.payment_method !== 'bank_transfer' && <>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>Non-bankable cheque favouring "{opt.favouring}"{f.payment_method === 'cheque_courier' ? ' — sent by courier' : ''}</div>
            <input ref={camRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
              onChange={async (e) => { const raw = e.target.files?.[0]; e.target.value = ''; if (!raw) return; const s = await shrink(raw); setChequeFile(s); if (chequePreview) URL.revokeObjectURL(chequePreview); setChequePreview(URL.createObjectURL(s)); }} />
            {chequePreview
              ? <div style={{ textAlign: 'center' }}><img src={chequePreview} alt="" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '10px' }} /><div><button style={S.link} onClick={() => camRef.current?.click()}>Retake photo</button></div></div>
              : <button style={{ ...S.ghost, width: '100%', borderStyle: 'dashed', borderColor: '#93c5fd', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }} onClick={() => camRef.current?.click()}>📷 Take a photo of the cheque</button>}
            <div style={S.two}>
              {I('Amount (₹)', f.amount, set('amount'), { type: 'number', inputMode: 'numeric' })}
              {I('Cheque no.', f.cheque_number, set('cheque_number'), { inputMode: 'numeric' })}
              {I('Bank', f.cheque_bank, set('cheque_bank'))}
              {I('Cheque date', f.cheque_date, set('cheque_date'), { type: 'date' })}
            </div>
            {I('Branch name & address', f.cheque_branch, set('cheque_branch'))}
            </>}
            {Number(f.amount) > 0 && <div style={{ color: '#16a34a', fontWeight: 700, marginTop: '4px' }}>₹{Number(f.amount).toLocaleString('en-IN')}</div>}

            <div style={S.h}>Source</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={S.chip(f.source === 'direct')} onClick={() => setF({ ...f, source: 'direct' })}>Direct</button>
              <button style={S.chip(f.source === 'channel_partner')} onClick={() => setF({ ...f, source: 'channel_partner' })}>Channel partner</button>
            </div>
            {f.source === 'channel_partner' && (
              <div style={{ ...S.card, marginTop: '10px' }}>
                <label style={S.lbl}>Channel partner</label>
                <select style={S.in} value={f.channel_partner_id || ''} onChange={set('channel_partner_id')}>
                  <option value="">— new partner (enter below) —</option>
                  {partners.map((p) => <option key={p.id} value={p.id}>{p.name}{p.firm ? ` · ${p.firm}` : ''}</option>)}
                </select>
                {!f.channel_partner_id && (
                  <div style={S.two}>
                    {I('Name *', f.broker_name, set('broker_name'))}
                    {I('Firm name', f.broker_firm, set('broker_firm'))}
                    {I('RERA no.', f.broker_rera, set('broker_rera'))}
                    {I('Mobile', f.broker_mobile, set('broker_mobile'), { inputMode: 'tel' })}
                  </div>
                )}
              </div>
            )}

            <div style={S.h}>Declaration</div>
            <ul style={{ margin: '0 0 8px 18px', padding: 0, fontSize: '14px', color: '#374151', lineHeight: 1.5 }}>
              {opt.declarations.map((d) => <li key={d}>{d}</li>)}
            </ul>
            <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '15px', fontWeight: 700, background: f.declaration_accepted ? '#ecfdf5' : '#fffbeb', padding: '12px', borderRadius: '10px', cursor: 'pointer' }}>
              <input type="checkbox" checked={f.declaration_accepted} onChange={set('declaration_accepted')} style={{ width: '22px', height: '22px', marginTop: '1px' }} />
              I / We submit this EOI voluntarily and without any influence, and agree to the points above.
            </label>

            <div style={S.h}>For office use</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '15px' }}>
              {[['pan', 'PAN card'], ['aadhaar', 'Aadhaar card'], ['cheque', 'Cheque']].map(([k, l]) => (
                <label key={k} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input type="checkbox" checked={!!f.office_checks[k] || (k === 'cheque' && !!chequeFile)} onChange={(e) => setF({ ...f, office_checks: { ...f.office_checks, [k]: e.target.checked } })} /> {l} received
                </label>
              ))}
            </div>

            {err && <div style={S.err}>{err}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '18px' }}>
              <button style={S.ghost} onClick={() => { setErr(''); setStep(1); }}>← Back</button>
              <button style={{ ...S.green, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={save}>{busy ? 'Saving…' : eoiId ? 'Save changes' : 'Save EOI'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EOIForm;
